import { createContext } from 'react';
import type { IFormOperationOptions } from '@utils/formOperationTracker';

export const createRowFormOperations = () => {
    let pending: Promise<unknown> = Promise.resolve();
    let failure: unknown;
    let hasFailed = false;
    let count = 0;
    const active = new Set<Promise<unknown>>();
    return {
        get isPending() {
            return count > 0 || active.size > 0;
        },
        get hasFailed() {
            return hasFailed;
        },
        track<T>(operation: () => Promise<T>, { critical = true }: IFormOperationOptions = {}): Promise<T> {
            const result = Promise.resolve().then(operation);
            const settled = result.then(
                () => {
                    active.delete(settled);
                },
                (error: unknown) => {
                    if (critical) {
                        hasFailed = true;
                        failure = error;
                    }
                    active.delete(settled);
                },
            );
            active.add(settled);
            return result;
        },
        run<T>(operation: () => Promise<T>): Promise<T> {
            count++;
            const result = pending.then(operation);
            pending = result.then(
                () => {
                    count--;
                },
                (error: unknown) => {
                    count--;
                    hasFailed = true;
                    failure = error;
                },
            );
            return result;
        },
        async flush() {
            // An operation can schedule another operation before it settles.
            let current: Promise<unknown>;
            do {
                current = pending;
                await Promise.all([current, ...active]);
            } while (current !== pending || active.size > 0);
            // Неудачный каскад мог частично изменить черновик. Успех другой
            // операции не доказывает, что эти изменения теперь согласованы.
            if (hasFailed) throw failure;
        },
    };
};

export const RowFormOperationsContext = createContext<ReturnType<typeof createRowFormOperations> | null>(null);
